import {useState} from "react";
import {Media} from "@/types/Media";
import {arrayMove} from "@dnd-kit/sortable";
import MultiUpload from "@/Organisms/MultiUpload/MultiUpload";
import confirm from "@/Atoms/AlertDialog/Confirm";
import {AxiosProgressEvent, AxiosRequestConfig} from "axios";


export default (
  {
    items,
    onUpload,
    onProgress,
    onReorder,
    onDelete,
    onChange,
    allowPdf,
    acceptedFileTypes,
    limitLabel,

  }: {
    items: Media[]
    onUpload: (file: File, id: string, index: number, options: AxiosRequestConfig) => Promise<any>
    onProgress: (percent: number, id: string, index: number) => void;
    onChange: (items: Media[] | ((prev: Media[]) => Media[])) => void;
    onDelete?: (media: Media) => Promise<any>
    onReorder?: (indices: (string | number)[]) => Promise<any>
    allowPdf?: boolean
    acceptedFileTypes?: { [mimeType: string]: string[] }
    limitLabel?: string
  }
) => {

  const [error, setError] = useState<string|undefined>()

  const handleUpload = async (file: File, id: string, index: number) => {
    setError(undefined)

    try {
      const data = await onUpload(file, id, index, {
        headers: {'Content-Type': 'multipart/form-data'},
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {

          const percent = Math.round(progressEvent.progress ? progressEvent.progress * 100 : 0);
          onProgress(percent, id, index)

        },
      })
      onChange((prev) => {


        const origIndex = prev.findIndex((item) => item.id === id)
        // for data.items (plural) : 1) remove temporary image at index and 2) insert uploaded images at specific index
        // for single item: 1) replace temporary image and mark as completed

        const newItems = data.images ?? [data];

        if (origIndex === -1) {
          // temporary placeholder was not found (e.g. due to a stale state update),
          // append instead of overwriting an unrelated (existing) item.
          return [...prev, ...newItems];
        }

        // prev.splice(origIndex, 1, ...(data.images ?? [{...prev[index], ...data, status: 'completed', progress: null}]));
        prev.splice(origIndex, 1, ...newItems);
        return prev;
      })
    } catch (e: any) {
      onChange((prev) => [...prev.filter((item) => item.id !== id)])
      setError(e.response?.data.message ?? e.message)
    }
  };
  const handleReorder = async (oldIndex: number, newIndex: number) => {

    const newItems = arrayMove(items, oldIndex, newIndex);
    onChange(newItems);
    try {
      const indices = newItems.map(item => item.id);

      if (onReorder !== undefined) {
        await onReorder(indices)
      }

    } catch (e) {
      console.log(e)
    }
  };

  const handleDelete = async (media: Media, index: number) => {

    const isConfirmed = await confirm({
      text: 'Weet je zeker dat je deze advertentie wilt verwijderen?'
    })

    if (!isConfirmed){
      return;
    }

    try {
      if (onDelete != undefined) {
        await onDelete(media)
      }

      // Use a functional update matched by id (instead of the closured `items`/`index`)
      // so we always operate on the latest state, even if it changed while the confirm
      // dialog (or the onDelete request) was pending.
      onChange((prev) => prev.filter((item) => item.id !== media.id))
    } catch (e) {
      console.log(e)
    }
  }

  return <MultiUpload
    items={items}
    allowPdf={allowPdf}
    limitLabel={limitLabel}
    acceptedFileTypes={acceptedFileTypes}
    error={error}
    onChange={items => onChange(items)}
    onUpload={handleUpload}
    onDelete={handleDelete}
    onReorder={handleReorder}
  />
}
